import React, { useState, useEffect, useCallback } from 'react';
import { getPublishers, createPublisher, register } from '../services/api';

function AdminDashboard() {
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePub, setShowCreatePub] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [pubForm, setPubForm] = useState({ name: '', domains: '' });
  const [userForm, setUserForm] = useState({ email: '', password: '', role: 'publisher', publisherId: '' });
  const [error, setError] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  const loadPublishers = useCallback(async () => {
    try {
      const res = await getPublishers();
      setPublishers(res.data.publishers);
    } catch (err) {
      setError('Failed to load publishers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPublishers(); }, [loadPublishers]);

  const handleCreatePublisher = async (e) => {
    e.preventDefault();
    try {
      await createPublisher({
        name: pubForm.name,
        domains: pubForm.domains.split(',').map((d) => d.trim()).filter(Boolean),
      });
      setPubForm({ name: '', domains: '' });
      setShowCreatePub(false);
      loadPublishers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create publisher');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await register(userForm);
      setUserForm({ email: '', password: '', role: 'publisher', publisherId: '' });
      setShowCreateUser(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const getCollectorSnippet = (pub) =>
    `<script>
window.__PREBID_MONITOR__ = {
  endpoint: '${window.location.origin}/api/logs',
  apiKey: '${pub.apiKey}',
  sampleRate: 1.0
};
</script>
<script src="${window.location.origin}/collector.js"></script>`;

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="section-header">
        <h2>Publishers</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreatePub(true)}>
            + Add Publisher
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowCreateUser(true)}>
            + Add User
          </button>
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {publishers.length === 0 ? (
        <div className="empty-state">
          <h3>No Publishers Yet</h3>
          <p>Add your first publisher to get started.</p>
        </div>
      ) : (
        <div className="publisher-grid">
          {publishers.map((pub) => (
            <div key={pub._id} className="publisher-card">
              <h3>{pub.name}</h3>
              <div className="meta">
                Status: <span className={`badge badge-${pub.status === 'active' ? 'info' : 'warning'}`}>{pub.status}</span>
                {' | '}Logs: {pub.logCount?.toLocaleString() || 0}
                {pub.domains?.length > 0 && <><br />Domains: {pub.domains.join(', ')}</>}
              </div>
              <div className="api-key">
                API Key: {pub.apiKey}
                <button
                  className="btn btn-sm btn-secondary"
                  style={{ marginLeft: 8 }}
                  onClick={() => copyToClipboard(pub.apiKey, pub._id)}
                >
                  {copiedKey === pub._id ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <details>
                <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#636e72' }}>
                  Collector Snippet
                </summary>
                <div className="snippet-box">
                  {getCollectorSnippet(pub)}
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(getCollectorSnippet(pub), pub._id + '-snippet')}
                  >
                    {copiedKey === pub._id + '-snippet' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </details>
            </div>
          ))}
        </div>
      )}

      {/* Create Publisher Modal */}
      {showCreatePub && (
        <div className="modal-overlay" onClick={() => setShowCreatePub(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add Publisher</h2>
            <form onSubmit={handleCreatePublisher}>
              <div className="form-group">
                <label>Publisher Name</label>
                <input
                  type="text"
                  value={pubForm.name}
                  onChange={(e) => setPubForm({ ...pubForm, name: e.target.value })}
                  placeholder="e.g., News18 Bengali"
                  required
                />
              </div>
              <div className="form-group">
                <label>Domains (comma-separated)</label>
                <input
                  type="text"
                  value={pubForm.domains}
                  onChange={(e) => setPubForm({ ...pubForm, domains: e.target.value })}
                  placeholder="e.g., bengali.news18.com, news18.com"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreatePub(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="modal-overlay" onClick={() => setShowCreateUser(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add User</h2>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                  <option value="publisher">Publisher</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Assign Publisher</label>
                <select value={userForm.publisherId} onChange={(e) => setUserForm({ ...userForm, publisherId: e.target.value })}>
                  <option value="">-- None --</option>
                  {publishers.map((p) => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateUser(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
